import { RefreshCw, Text } from 'lucide-react'
import { Button, Tooltip } from '@highlight/ui'
import { cheapestPlanWith, type BillingPlanV2Tier } from '@highlight/common'
import { usePricingEdition } from '@renderer/chat/hooks/use-plan-features'
import { usePlanAllowances, type AllowanceCount } from '@renderer/chat/hooks/use-plan-allowances'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { subscriptionPlansRoute } from '@shared/settings/settings-deeplink'
import { openSubscriptionSettings } from '../../../components/sidepanel/plan-banners/sidepanel-plan-banners'
import { ReadingOrangeLoadstone } from './preparing-indicator'
import { useBrief } from '../../../components/meeting/brief/use-brief'
import { useMeetingBriefGenerationEnabled } from '../../../components/meeting/brief/use-meeting-brief-generation-enabled'
import {
  MEETING_HOURS_EXHAUSTED_TOOLTIP,
  useMeetingHoursExhausted,
} from '../../../components/meeting/brief/use-meeting-hours-exhausted'
import type { MeetingViewModel } from '../use-meeting-view-model'

/**
 * Whether this meeting can have a brief generated. A brief prepares you for a
 * meeting, so there's nothing to prepare for once it's over; Custom Meetings
 * have no calendar event to key one on.
 *
 * A pure derivation, split from `useMeetingBriefGeneration` so layout-only
 * consumers (the header's CTA row) can gate on it without instantiating
 * `useBrief`'s conversation watcher and mutation just to read a boolean.
 */
export function useCanGenerateBrief(model: MeetingViewModel): boolean {
  const isEnabled = useMeetingBriefGenerationEnabled()
  const hasEnded = !!model.endedAt && model.endedAt.getTime() < Date.now()
  return isEnabled && !!model.briefCalendarEventId && !hasEnded
}

/**
 * Free's briefs this period, null on a plan that briefs every meeting, or
 * undefined while the count is unknown (loading, or a failed read): until it's
 * known a paid plan and Free look alike, and a brief generated then is spent
 * without its count in view.
 */
export function useMeetingBriefAllowance(): AllowanceCount | null | undefined {
  const { isLoading: isPlanLoading } = usePaywallState()
  const { data, isLoading, isError } = usePlanAllowances()
  if (isPlanLoading) return undefined
  if (data) return data.meetingBriefs
  // The query only runs on a capped plan, so an unread count there is unknown, not uncapped.
  return isLoading || isError ? undefined : null
}

/** The cheapest plan that briefs every meeting: what a spent brief allowance sells. */
export function useBriefsUpgradePlan(): BillingPlanV2Tier {
  return cheapestPlanWith(usePricingEdition(), (features) => features.monthlyMeetingBriefs === null)
}

/** Opens the plans page from a spent brief allowance. */
export function upgradeForBriefs(plan: BillingPlanV2Tier, from: string) {
  AmplitudeRenderer.trackEvent('Paywall', { action: 'clicked', feature: 'meeting briefs', plan, from })
  openSubscriptionSettings(subscriptionPlansRoute())
}

/**
 * The brief-generation actions plus their in-flight state.
 *
 * Deliberately an explicit action rather than a side effect of opening the
 * Meeting View: every meeting click now drills into the view, so generating on
 * open would spawn an agent run for each meeting merely browsed. The pre-meeting
 * auto-trigger (T-20min, in the main process) and the brief entry point still
 * generate on their own — this is the way in for everything they skip.
 */
export function useMeetingBriefGeneration(model: MeetingViewModel) {
  const canGenerate = useCanGenerateBrief(model)
  const outOfHours = useMeetingHoursExhausted()
  const { create, regenerate, isTriggerPending } = useBrief(model.briefCalendarEventId)

  return {
    canGenerate,
    // `canGenerate` hides a control with nothing to act on (no calendar event,
    // meeting over). This one leaves the control visible but inert, so the
    // tooltip can explain a refusal the user can actually fix.
    outOfHours,
    isGenerating: model.briefGenerating || isTriggerPending,
    create,
    regenerate,
  }
}

export function trackGeneration(action: 'generate' | 'regenerate', calendarEventId: string | undefined) {
  AmplitudeRenderer.trackEvent('Meeting Brief', {
    action,
    source: 'meeting-view',
    meeting_id: calendarEventId,
  })
}

/**
 * The header's brief slot when there's no brief yet: the in-flight status while
 * one is generating, otherwise the way to ask for one. Renders nothing when a
 * brief can't be generated, so a Custom Meeting or a meeting that's over doesn't
 * advertise an action that would do nothing.
 *
 * Sized to share the header CTA row with the Join button (brief left, Join
 * right) — the row and its gating live in MeetingViewHeader.
 */
export function MeetingBriefGenerationPrompt({ model }: { model: MeetingViewModel }) {
  const { canGenerate, outOfHours, isGenerating, create } = useMeetingBriefGeneration(model)
  const briefs = useMeetingBriefAllowance()

  // Only an uncapped plan gets the plain prompt: Free spends its briefs from LockedBrief, with the count in view.
  if (!canGenerate || briefs !== null) {
    return null
  }

  const generateBrief = () => {
    create()
    trackGeneration('generate', model.briefCalendarEventId)
  }

  // One button for both states — disabling in place instead of swapping to a
  // status pill keeps the control's identity (and focus target) stable.
  // The Tooltip wraps the Button rather than sitting on it. A natively-disabled
  // button fires no pointer events, so the wrapper is what stays hoverable.
  return (
    <Tooltip disabled={!outOfHours}>
      <Tooltip.Trigger render={<div className="relative flex w-full" />}>
        <Button
          variant="outline"
          className="w-full"
          disabled={isGenerating || outOfHours}
          aria-busy={isGenerating}
          onClick={generateBrief}
        >
          {isGenerating ? (
            <>
              <ReadingOrangeLoadstone size={16} />
              Preparing
            </>
          ) : (
            <>
              <Text aria-hidden="true" />
              Prepare Brief
            </>
          )}
        </Button>
      </Tooltip.Trigger>
      <Tooltip.Content>{MEETING_HOURS_EXHAUSTED_TOOLTIP}</Tooltip.Content>
    </Tooltip>
  )
}

/** Regenerate action for the brief body's toolbar, beside copy. */
export function RegenerateBriefButton({ model }: { model: MeetingViewModel }) {
  const { canGenerate, outOfHours, isGenerating, regenerate } = useMeetingBriefGeneration(model)

  if (!canGenerate) {
    return null
  }

  const tooltip = outOfHours ? MEETING_HOURS_EXHAUSTED_TOOLTIP : isGenerating ? 'Regenerating…' : 'Regenerate brief'

  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="relative flex" />}>
        <Button
          aria-label="Regenerate brief"
          shape="square"
          variant="ghost"
          disabled={isGenerating || outOfHours}
          onClick={() => {
            regenerate()
            trackGeneration('regenerate', model.briefCalendarEventId)
          }}
        >
          <RefreshCw aria-hidden="true" className={isGenerating ? 'animate-spin' : undefined} />
        </Button>
      </Tooltip.Trigger>
      <Tooltip.Content>{tooltip}</Tooltip.Content>
    </Tooltip>
  )
}
